import React, { useMemo, useState, useCallback, useEffect } from "react";

function ColorTile({ id, label, colorClass, picked, onToggle, token, showTokens }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={[
        "rounded-xl border p-3 flex items-center justify-between gap-3 transition",
        "w-full",
        picked
          ? "border-emerald-300/60 bg-emerald-500/10"
          : "border-white/10 bg-white/5 hover:bg-white/10",
      ].join(" ")}
    >
      {/* carré couleur (+ token si mode daltonien) */}
      <div className={["relative w-10 h-10 rounded-lg border border-black/10", colorClass].join(" ")}>
        {showTokens && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[12px] font-mono font-bold text-black/70">
              {token}
            </span>
          </div>
        )}
      </div>

      <div className="text-xs text-slate-200 flex-1 text-left">
        {label}
        {showTokens && (
          <span className="text-slate-400 font-mono"> ({token})</span>
        )}
      </div>

      <div className="text-[11px] text-slate-400 font-mono">{picked ? "✓" : ""}</div>
    </button>
  );
}

function SequenceSlot({ colorClass, empty, token, showTokens }) {
  return (
    <div
      className={[
        "relative w-10 h-10 rounded-lg border flex items-center justify-center",
        empty ? "border-white/10 bg-white/5" : "border-black/10",
        empty ? "" : colorClass,
      ].join(" ")}
      aria-label={empty ? "Emplacement vide" : `Post-it ${showTokens ? token : ""}`}
      title={empty ? "" : showTokens ? `Post-it ${token}` : ""}
    >
      {!empty && showTokens && (
        <span className="text-[12px] font-mono font-bold text-black/70">
          {token}
        </span>
      )}
    </div>
  );
}

function Peg({ state, showTokens }) {
  const cls =
    state === "g"
      ? "bg-emerald-400"
      : state === "y"
        ? "bg-amber-300"
        : "bg-red-400";

  // symbole uniquement si mode daltonien (sinon on garde tes couleurs “propres”)
  const symbol = state === "g" ? "✓" : state === "y" ? "~" : "×";
  const aria =
    state === "g"
      ? "Bon endroit"
      : state === "y"
        ? "Bonne couleur mauvais endroit"
        : "Absent";

  return (
    <div
      className={[
        "w-3 h-3 rounded-full border border-black/10 flex items-center justify-center",
        cls,
      ].join(" ")}
      aria-label={aria}
      title={aria}
    >
      {showTokens && (
        <span className="text-[9px] font-bold text-black/70 leading-none">
          {symbol}
        </span>
      )}
    </div>
  );
}

// ✅ shuffle simple (Fisher-Yates) — retourne une copie
function shuffleCopy(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function PostitOrderMiniPuzzle({ solvedProp = false, onSolved, draft, onDraftChange, }) {
  // ✅ garde ta bonne solution
  const CORRECT = useMemo(() => ["green", "pink", "blue", "yellow"], []);

  // ✅ base items (inchangé) + tokens (utilisés uniquement si mode daltonien)
  const BASE_ITEMS = useMemo(
    () => [
      // vraies
      { id: "yellow", label: "Post-it jaune", colorClass: "bg-amber-300", token: "J" },
      { id: "pink", label: "Post-it rose", colorClass: "bg-pink-300", token: "R" },
      { id: "green", label: "Post-it vert", colorClass: "bg-emerald-300", token: "Ve" },
      { id: "blue", label: "Post-it bleu", colorClass: "bg-sky-300", token: "B" },

      // parasites
      { id: "purple", label: "Post-it violet", colorClass: "bg-purple-300", token: "Vi" },
      { id: "orange", label: "Post-it orange", colorClass: "bg-orange-300", token: "O" },
      { id: "gray", label: "Post-it gris", colorClass: "bg-slate-300", token: "G" },
    ],
    []
  );

  // ✅ ITEMS mélangés (stable : une seule fois par montage du composant)
  const ITEMS = useMemo(() => shuffleCopy(BASE_ITEMS), [BASE_ITEMS]);

  const [solved, setSolved] = useState(false);
  const seq = draft?.seq ?? [];
  const tries = draft?.tries ?? [];
  const feedback = draft?.feedback ?? "";

  const setSeq = (next) =>
    onDraftChange?.((d) => ({ ...d, seq: typeof next === "function" ? next(d.seq ?? []) : next }));

  const setTries = (next) =>
    onDraftChange?.((d) => ({ ...d, tries: typeof next === "function" ? next(d.tries ?? []) : next }));

  const setFeedback = (next) =>
    onDraftChange?.((d) => ({ ...d, feedback: typeof next === "function" ? next(d.feedback ?? "") : next }));

  // ✅ Toggle daltonien (persisté localStorage)
  const [colorblindMode, setColorblindMode] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("postitPuzzle:colorblind");
      if (saved === "1") setColorblindMode(true);
    } catch {
      // ignore (SSR / privacy mode)
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("postitPuzzle:colorblind", colorblindMode ? "1" : "0");
    } catch {
      // ignore
    }
  }, [colorblindMode]);

  useEffect(() => {
    if (!solvedProp) return;
    setSolved(true);
    // setFeedback("✅ Déjà résolu !");
    // optionnel : afficher directement la bonne suite
    setSeq(CORRECT);
  }, [solvedProp, CORRECT]);

  const showTokens = colorblindMode;

  const colorClassById = useCallback(
    (id) => ITEMS.find((x) => x.id === id)?.colorClass,
    [ITEMS]
  );

  const tokenById = useCallback(
    (id) => ITEMS.find((x) => x.id === id)?.token || "?",
    [ITEMS]
  );

  const evalGuess = useCallback((guess, target) => {
    const pegs = new Array(guess.length).fill("r");

    const remainingTarget = {};
    for (let i = 0; i < guess.length; i++) {
      if (guess[i] === target[i]) {
        pegs[i] = "g";
      } else {
        remainingTarget[target[i]] = (remainingTarget[target[i]] || 0) + 1;
      }
    }

    for (let i = 0; i < guess.length; i++) {
      if (pegs[i] === "g") continue;
      const c = guess[i];
      if (remainingTarget[c] > 0) {
        pegs[i] = "y";
        remainingTarget[c] -= 1;
      }
    }

    return pegs;
  }, []);

  const toggle = useCallback((id) => {
    if (solved || solvedProp) return;
    setFeedback("");
    setSeq((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  }, []);

  const reset = useCallback(() => {
  if (solved || solvedProp) return;
  setSeq([]);
  setFeedback("");
}, [solved, solvedProp]);


  const submit = useCallback(() => {
    if (solved || solvedProp) return;
    if (seq.length !== 4) {
      setFeedback("❌ Il faut 4 post-its dans la suite.");
      return;
    }

    const pegs = evalGuess(seq, CORRECT);
    setTries((prev) => [...prev, { seq: [...seq], pegs }]);

    const ok = seq.every((v, i) => v === CORRECT[i]);
    if (ok) {
      setSolved(true);
      setFeedback("✅ Trouvé !");
      onSolved?.();
    } else {
      setFeedback("❌ Ce n’est pas le bon ordre.");
      setSeq([]);
    }
  }, [seq, CORRECT, evalGuess, onSolved]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-300">
        Une note froissée :{" "}
        <span className="text-slate-100 font-semibold">“L’ordre est important. Ne pas se fier au mur.”</span>
      </p>
      <p className="text-xs text-slate-300">C'est signé "Samir"</p>

      <div className="rounded-xl border border-white/10 bg-black/30 p-3">
        {/* ✅ Toggle discret */}
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
            Suite reconstituée
          </p>

          <button
            type="button"
            onClick={() => setColorblindMode((v) => !v)}
            className={[
              "rounded-full px-3 py-1 text-[11px] border transition",
              colorblindMode
                ? "bg-indigo-500/15 border-indigo-400/40 text-indigo-100"
                : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10",
            ].join(" ")}
            aria-pressed={colorblindMode}
          >
            {colorblindMode ? "Mode daltonien : ON" : "Mode daltonien"}
          </button>
        </div>

        <div className="flex gap-2">
          {[0, 1, 2, 3].map((i) => {
            const id = seq[i];
            return (
              <SequenceSlot
                key={i}
                empty={!id}
                colorClass={id ? colorClassById(id) : ""}
                token={id ? tokenById(id) : ""}
                showTokens={showTokens}
              />
            );
          })}
        </div>

        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={reset}
            disabled={(solved || solvedProp)}
            className={`${(solved || solvedProp) ? "disabled:cursor-not-allowed disabled:opacity-50" : ""} flex-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 py-2 text-xs font-semibold`}
          >
            Effacer
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={(solved || solvedProp)}
            className={`${(solved || solvedProp) ? "disabled:cursor-not-allowed disabled:opacity-50" : ""} flex-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 py-2 text-xs font-semibold`}
          >
            Vérifier
          </button>
        </div>

        {feedback && (
          <p className={["mt-2 text-xs", feedback.startsWith("✅") ? "text-emerald-300" : "text-amber-200"].join(" ")}>
            {feedback}
          </p>
        )}

        {tries.length > 0 && (
          <div className="mt-3 space-y-2">
            {tries
              .slice()
              .reverse()
              .map((t, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border border-white/10 bg-white/5 p-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex gap-2">
                      {t.seq.map((id, i) => (
                        <SequenceSlot
                          key={i}
                          empty={false}
                          colorClass={colorClassById(id)}
                          token={tokenById(id)}
                          showTokens={showTokens}
                        />
                      ))}
                    </div>

                    <div className="grid grid-cols-4 gap-1">
                      {t.pegs.map((p, i) => (
                        <Peg key={i} state={p} showTokens={showTokens} />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {!solved && (
        <div className="space-y-2">
          <p className="text-[11px] text-slate-400">
            Clique pour construire la suite (recliquer = retirer).
          </p>
          <div className="grid grid-cols-1 gap-2">
            {ITEMS.map((it) => (
              <ColorTile
                key={it.id}
                id={it.id}
                label={it.label}
                colorClass={it.colorClass}
                picked={seq.includes(it.id)}
                onToggle={toggle}
                token={it.token}
                showTokens={showTokens}
              />
            ))}
          </div>
        </div>
      )}

      {solved && (
        <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3">
          <p className="text-sm font-semibold text-emerald-200">
            C'est la bonne combinaison, mais à qui ça pourrait bien servir ?
          </p>
        </div>
      )}
    </div>
  );
}
